import React from "react";
import { useParams } from "react-router-dom";

// Components
import SEO from "components/CBSEO";
import SchemaComponent from "components/CBSchema";
import DynamicHero from "components/DynamicHero";
import TextBlock from "components/TextBlock";
import { ImageContainer } from "styles/components/imageContainer";

// Styled Components
import { StyledPageContainer } from "styles/components/PageContainer/index.styles";
import { TwoColumnSection } from "styles/components/twoColumnSection";

// Utils
import { constructSchemaData } from "utils/helpers/constructSchemaData";

// Hook
import useDynamicPageRenderer from "utils/hooks/useDynamicPageRenderer";

// Content Data
import aetnaContent from "./data/pageContentData/aetnaContent.data.json";
import anthemContent from "./data/pageContentData/anthemContent.data.json";
import cignaContent from "./data/pageContentData/cignaContent.data.json";
import oscarContent from "./data/pageContentData/oscarContent.data.json";
import oxfordContent from "./data/pageContentData/oxfordContent.data.json";
import unitedHealthcareContent from "./data/pageContentData/unitedHealthCareContent.data.json";

// SEO Data
import aetnaSeoData from "./data/seoData/aetnaSeo.data.json";
import anthemSeoData from "./data/seoData/anthemSeo.data.json";
import cignaSeoData from "./data/seoData/cignaSeo.data.json";
import oscarSeoData from "./data/seoData/oscarSeo.data.json";
import oxfordSeoData from "./data/seoData/oxfordSeo.data.json";
import unitedHealthcareSeoData from "./data/seoData/unitedHealthcareSeo.data.json";

import Page404 from "pages/404";
import TherapyOptions from "components/TherapyOptions";
import ResourcesSlider from "pages/TherapyHub/components/resourcesSlider";
import StartYourJourney from "pages/TherapyHub/components/startYourJourney";
import FAQContent from "components/CBFAQ";
import GetStartedToday from "pages/TherapyHub/components/GetStartedToday";
import CBDivider from "components/CBDivider";

// Data Maps - mapping insuranceType to respective data
const insuranceContentMap = {
  "aetna": aetnaContent,
  "anthem": anthemContent,
  "cigna": cignaContent,
  "oscar": oscarContent,
  "oxford": oxfordContent,
  "united-healthcare": unitedHealthcareContent,
};

const insuranceSeoDataMap = {
  "aetna": aetnaSeoData,
  "anthem": anthemSeoData,
  "cigna": cignaSeoData,
  "oscar": oscarSeoData,
  "oxford": oxfordSeoData,
  "united-healthcare": unitedHealthcareSeoData,
};

// Components that expect props to be spread directly
const SPREAD_PROPS_COMPONENTS = ["DynamicHero", "TextBlock", "ThreeColumnSection", "Testimonials", "HowItWorks"];

// Component Map - mapping component type strings to React components
const componentMap = {
  "DynamicHero": DynamicHero,
  "TextBlock": TextBlock,
  "TwoColumnSection": TwoColumnSection,
  "ImageContainer": ImageContainer,
  "TherapyOptions": TherapyOptions,
  "ResourcesSlider": ResourcesSlider,
  "StartYourJourney": StartYourJourney,
  "Faq": FAQContent,
  "GetStartedToday": GetStartedToday,
  "Divider": CBDivider,
};

// Helper function to format modality type for display
const formatModalityType = (modalityType) => {
  return modalityType
    ?.split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// Helper function to format insurance name for display
const formatInsuranceName = (insuranceType) => {
  return insuranceType
    ?.split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const InsuranceOptionsDetails = () => {
  const { modalityType, insuranceType } = useParams();
  const content = insuranceContentMap[insuranceType] || [];
  const baseSeoData = insuranceSeoDataMap[insuranceType] || {};

  // Create dynamic breadcrumbs based on modalityType presence
  const formattedModality = formatModalityType(modalityType);
  const formattedInsurance = formatInsuranceName(insuranceType);

  const dynamicBreadcrumbs = modalityType
    ? [
        { name: "Home", url: "/" },
        { name: formattedModality, url: `/${modalityType}` },
        { name: `${formattedModality} Insurance Options`, url: `/${modalityType}/insurance-options` },
        { name: `${formattedInsurance} Insurance Coverage`, url: `/${modalityType}/insurance-options/${insuranceType}` },
      ]
    : [
        { name: "Home", url: "/" },
        { name: "Insurance Options", url: "/insurance-options" },
        { name: `${formattedInsurance} Coverage`, url: `/insurance-options/${insuranceType}` },
      ];

  // Create dynamic canonical URL - handles both patterns
  const dynamicCanonicalUrl = `https://www.almadelic.com/${
    modalityType ? `${modalityType}/insurance-options/${insuranceType}` : `insurance-options/${insuranceType}`
  }`;

  // Create dynamic SEO data
  const dynamicSeoData = {
    ...baseSeoData,
    title: modalityType
      ? baseSeoData.title?.replace("| Almadelic", `| ${formattedModality} | Almadelic`) ||
        `${formattedInsurance} Insurance Coverage for ${formattedModality} | Almadelic`
      : baseSeoData.title || `${formattedInsurance} Insurance Coverage | Almadelic`,
    description: modalityType
      ? baseSeoData.description ||
        `Use your ${formattedInsurance} insurance benefits for ${modalityType} and mental health treatment. Learn about coverage options and benefits with Almadelic.`
      : baseSeoData.description ||
        `Use your ${formattedInsurance} insurance benefits for mental health treatment. Learn about coverage options and benefits with Almadelic.`,
    canonicalUrl: dynamicCanonicalUrl,
  };

  // Create dynamic schema data - no need for static schema files
  const dynamicSchemaData = constructSchemaData(dynamicCanonicalUrl, dynamicBreadcrumbs);

  const renderedComponents = useDynamicPageRenderer(content, componentMap, SPREAD_PROPS_COMPONENTS);

  if (!content || content.length === 0) {
    return <Page404 />;
  }

  return (
    <StyledPageContainer id={`${modalityType ? `${modalityType}-` : ""}${insuranceType}-insurance-options-details-page-container`}>
      <SEO title={dynamicSeoData.title} description={dynamicSeoData.description} canonicalUrl={dynamicSeoData.canonicalUrl} />
      <SchemaComponent schemas={dynamicSchemaData} />

      {renderedComponents}
    </StyledPageContainer>
  );
};

export default InsuranceOptionsDetails;

// TwoColumnSection (2 cols span 6/6)
// TwoColumnSection (single col spans 12)
// resourceSlider
